import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, ArrowRight, Banknote, CheckCircle2, CircleOff, FileText,
  GitBranch, Megaphone, RefreshCw, ShieldCheck, Sparkles, Users,
} from "lucide-react";
import api from "@/lib/api";

const SOURCES = [
  ["drafts", "/platform/campaign-director/drafts"],
  ["campaigns", "/platform/lead-marketplace/campaigns"],
  ["leads", "/platform/lead-marketplace/leads"],
  ["buyers", "/platform/lead-buyers"],
  ["orders", "/platform/lead-buyers/orders"],
  ["capture", "/platform/lead-marketplace/capabilities"],
  ["checkout", "/platform/lead-buyers/capabilities"],
];

const STEPS = [
  {
    number:"01", label:"Strategize", title:"Campaign Director",
    description:"Generate and review Florida Final Expense campaign messaging.",
    to:"/campaign-director", action:"Open AI campaign drafts", icon:Sparkles,
    state:"Draft generation available on explicit request",
  },
  {
    number:"02", label:"Acquire", title:"Lead acquisition",
    description:"Configure a reviewed landing page and consumer-permission flow.",
    to:"/lead-marketplace", action:"Manage landing campaigns", icon:Megaphone,
    state:"No connected ad publisher or organic content engine",
  },
  {
    number:"03", label:"Qualify", title:"Lead inventory",
    description:"Review consent, contact evidence, qualification and suppression.",
    to:"/lead-marketplace", action:"Review captured inquiries", icon:ShieldCheck,
    state:"Human verification required before reservation",
  },
  {
    number:"04", label:"Monetize", title:"Buyer marketplace",
    description:"Verify agency buyers, prepare fixed-price packages and track funded orders.",
    to:"/lead-buyers", action:"Manage buyers and orders", icon:Banknote,
    state:"No automated buyer PII delivery in this release",
  },
];

const formatMoney = cents => new Intl.NumberFormat("en-US",{
  style:"currency",currency:"USD",maximumFractionDigits:0,
}).format(cents / 100);

function Metric({label,value,description,icon:Icon,loading=false,ready=true}) {
  return (
    <div className="min-w-0 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium uppercase tracking-[0.11em] text-slate-500">{label}</span>
        <Icon className="h-[18px] w-[18px] text-[#AC8441]" aria-hidden="true"/>
      </div>
      <div className="mt-3 font-heading text-3xl font-bold tracking-tight text-[#0B1B2C]"
        data-testid={`growth-metric-${label.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
        {loading ? "…" : ready ? value : "—"}
      </div>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">{description}</p>
    </div>
  );
}

function Status({ready,known=true,disabledLabel,enabledLabel}) {
  return (
    <span className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${ready
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : "border-slate-200 bg-slate-50 text-slate-600"}`}>
      {ready ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0"/> : <CircleOff className="h-3.5 w-3.5 shrink-0"/>}
      {!known ? "Unavailable" : ready ? enabledLabel : disabledLabel}
    </span>
  );
}

export default function GrowthCommandCenter() {
  const [values,setValues] = useState({});
  const [loadState,setLoadState] = useState("loading");

  const load = useCallback(async () => {
    setLoadState("loading");
    const results = await Promise.allSettled(SOURCES.map(([,route])=>api.get(route)));
    const next = Object.fromEntries(SOURCES.map(([key],i)=>[
      key,results[i].status === "fulfilled" ? results[i].value.data : null,
    ]));
    setValues(next);
    setLoadState(results.every(result=>result.status==="fulfilled") ? "ready" : "partial");
  },[]);

  useEffect(()=>{load();},[load]);

  const drafts = Array.isArray(values.drafts) ? values.drafts : null;
  const campaigns = Array.isArray(values.campaigns) ? values.campaigns : null;
  const leads = Array.isArray(values.leads) ? values.leads : null;
  const buyers = Array.isArray(values.buyers) ? values.buyers : null;
  const orders = Array.isArray(values.orders) ? values.orders : null;
  const isLoading = loadState === "loading";
  const publicIntake = values.capture?.capture_enabled === true;
  const checkoutReady = values.checkout?.checkout_ready === true;
  const qualified = leads?.filter(lead=>lead.qualification_status === "qualified").length;
  const funded = orders?.filter(order=>order.status === "paid") || [];
  const paidGross = funded.reduce((sum,order)=>sum + (Number(order.amount_cents)||0),0);

  return (
    <main className="mx-auto w-full min-w-0 max-w-[1500px] space-y-7 pb-10" data-testid="growth-command-center-page">
      <section className="relative overflow-hidden rounded-[22px] bg-[#0B1B2C] px-5 py-7 text-white shadow-[0_16px_40px_-24px_rgba(11,27,44,0.7)] sm:px-8 sm:py-9">
        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full border border-[#D1A656]/20"
          aria-hidden="true"/>
        <div className="pointer-events-none absolute -right-8 -top-12 h-48 w-48 rounded-full border border-[#D1A656]/25"
          aria-hidden="true"/>
        <div className="relative">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E3D0AB]">Fortunate Operations</span>
            <span className="rounded-full border border-white/20 px-2.5 py-1 text-[10px] text-slate-300">Platform owner</span>
          </div>
          <div className="mt-4 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-[660px] min-w-0">
              <h1 className="font-heading text-[28px] font-bold leading-tight tracking-tight sm:text-[38px]">
                Growth Command Center
              </h1>
              <p className="mt-3 max-w-[640px] text-sm leading-6 text-slate-300 sm:text-base">
                From campaign strategy to verified inquiries and prepaid agency orders.
                Every stage links to an existing workflow. No fictional revenue or live marketing claims.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/campaign-launch"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#D1A656] px-4 py-2.5 text-sm font-semibold text-[#0B1B2C] transition hover:bg-[#E3D0AB] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                data-testid="growth-create-draft">
                <Sparkles className="h-4 w-4"/> Launch a campaign
              </Link>
              <button type="button" onClick={load} disabled={isLoading}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-white/25 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/10 disabled:opacity-50"
                data-testid="growth-refresh">
                <RefreshCw className={`h-4 w-4 ${isLoading?"animate-spin":""}`}/> Refresh metrics
              </button>
            </div>
          </div>
        </div>
      </section>

      {loadState==="partial" && (
        <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Some live owner data could not be retrieved. Unavailable values show a dash, not zero.
          Use Refresh metrics to retry.
        </div>
      )}

      <section aria-label="Real platform operating metrics">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#AC8441]">Operating snapshot</p>
            <h2 className="mt-1 font-heading text-xl font-bold text-[#0B1B2C]">Your growth pipeline</h2>
          </div>
          <p className="text-xs text-slate-500">Current records only · No estimated marketing returns</p>
        </div>
        <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric icon={FileText} label="AI drafts" value={drafts?.length??0}
            description="Saved campaign proposals" loading={isLoading} ready={!!drafts}/>
          <Metric icon={Megaphone} label="Active intake" value={campaigns?.filter(c=>c.status==="active").length??0}
            description="Active public landing campaigns" loading={isLoading} ready={!!campaigns}/>
          <Metric icon={ShieldCheck} label="Qualified inquiries" value={qualified??0}
            description="Human-reviewed inventory" loading={isLoading} ready={!!leads}/>
          <Metric icon={Banknote} label="Funded orders" value={orders ? formatMoney(paidGross) : "$0"}
            description="Gross paid order value, before fees, refunds or delivery" loading={isLoading} ready={!!orders}/>
        </div>
      </section>

      <section className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1.75fr)_minmax(290px,1fr)]">
        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-5 flex items-center gap-3">
            <div className="rounded-xl bg-[#0B1B2C] p-2.5 text-[#D1A656]"><GitBranch className="h-5 w-5"/></div>
            <div>
              <h2 className="font-heading text-lg font-bold text-[#0B1B2C]">One connected workflow</h2>
              <p className="text-xs text-slate-500">Use each stage below to reach its actual workspace.</p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {STEPS.map(step=>(
              <Link key={step.number} to={step.to}
                data-testid={`growth-step-${step.number}`}
                className="group flex min-w-0 flex-col rounded-xl border border-slate-200 bg-[#F8FAFB] p-4 transition hover:border-[#D1A656]/70 hover:bg-white hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#D1A656]">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold tracking-[0.15em] text-[#AC8441]">{step.number} / {step.label}</span>
                  <step.icon className="h-4 w-4 text-[#2C3B49]"/>
                </div>
                <h3 className="mt-3 font-heading text-base font-bold text-[#0B1B2C]">{step.title}</h3>
                <p className="mt-1 flex-1 text-xs leading-5 text-slate-600">{step.description}</p>
                <p className="mt-3 text-[11px] leading-4 text-slate-500">{step.state}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#0B1B2C]">
                  {step.action}<ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-1"/>
                </span>
              </Link>
            ))}
          </div>
        </div>

        <aside className="min-w-0 space-y-5">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-[#AC8441]"/>
              <h2 className="font-heading text-base font-bold text-[#0B1B2C]">Launch readiness</h2>
            </div>
            <div className="mt-4 space-y-3 text-xs">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-slate-700">Public intake configuration</span>
                <Status ready={publicIntake} known={!!values.capture} enabledLabel="Enabled in settings" disabledLabel="Disabled"/>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-slate-700">Buyer checkout configuration</span>
                <Status ready={checkoutReady} known={!!values.checkout} enabledLabel="Configured" disabledLabel="Not configured"/>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-slate-700">Verified buyers in CRM</span>
                <span className="font-semibold text-[#0B1B2C]">{isLoading?"…":buyers ? buyers.filter(b=>b.status==="verified").length : "—"}</span>
              </div>
            </div>
            <p className="mt-4 border-t border-slate-100 pt-4 text-xs leading-5 text-slate-500">
              Configuration status is not legal approval. Public sharing, licensing, buyer delivery,
              real payments, and paid media still require separate live verification.
            </p>
          </section>

          <section className="rounded-2xl border border-[#D1A656]/30 bg-[#FBF7EF] p-4 sm:p-5">
            <h2 className="font-heading text-base font-bold text-[#0B1B2C]">Marketing integrations</h2>
            <p className="mt-2 text-xs leading-5 text-slate-600">
              Campaign draft creation works on explicit request. These features are not included
              in the next production release:
            </p>
            <ul className="mt-3 space-y-2 text-xs text-slate-700">
              <li>Google / Meta campaign publishing and spend optimization</li>
              <li>AI visual creative and social content studio</li>
              <li>SEO / AI-search publishing and performance</li>
              <li>Automated secure lead delivery to buyer agencies</li>
            </ul>
          </section>
        </aside>
      </section>

      <p className="text-xs leading-5 text-slate-500">
        This page uses owner-scoped records and configuration endpoints. It does not trigger AI generation,
        ad spending, calls, payments or consumer outreach when opened.
      </p>
    </main>
  );
}
